import React, { memo, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MDBIcon, MDBBadge, MDBBtn } from 'mdbreact';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import overlayFactory from 'react-bootstrap-table2-overlay';
// Mis Componentes
import Tooltip from 'components/shared/Tooltip';
// Mis Types
import { Pagination } from 'typings/Tablas';
import './ChoferesTabla.css';

export interface ChoferesTablaProps {
   choferes: any[];
   isTableLoading: boolean;
   pagination: Pagination;
   handleTableChange: (
      type: any,
      {
         page,
         sizePerPage
      }: {
         page: any;
         sizePerPage: any;
      }
   ) => void;
   handleCastigarChofer: (id: number, castigado: boolean, index: number) => Promise<void>;
   handleToggleEstado: (id: number, activo: boolean, index: number, prefijo: string) => Promise<void>;
}

const ChoferesTabla: React.SFC<ChoferesTablaProps> = ({
   choferes,
   isTableLoading,
   pagination,
   handleTableChange,
   handleCastigarChofer,
   handleToggleEstado
}) => {
   const optionsFormatter = useCallback(
      (cell, row, rowIndex) => {
         return (
            <>
               <Tooltip title='Castigar/Perdonar' placement='top'>
                  <MDBBtn
                     color={row.options.castigado ? 'warning' : 'blue-grey'}
                     className='btn btn-sm my-0 mr-2 btn-action'
                     onClick={() => handleCastigarChofer(row.idChofer, row.options.castigado, rowIndex)}>
                     <MDBIcon size='2x' icon='exclamation-triangle' />
                  </MDBBtn>
               </Tooltip>
               <Tooltip title='Desactivar/Activar' placement='top'>
                  <MDBBtn
                     color={row.activo ? 'success' : 'danger'}
                     className='btn btn-sm my-0 mr-2 btn-action'
                     onClick={() => handleToggleEstado(row.idChofer, row.activo, rowIndex, row.zona.prefijoChofer)}>
                     <MDBIcon size='2x' icon='power-off' />
                  </MDBBtn>
               </Tooltip>
               <Link
                  className='btn btn-primary btn-sm mr-2 detalles-font-size text-capitalize'
                  to={`/r/choferes/${row.idChofer}/detalles`}>
                  Ver más
               </Link>
            </>
         );
      },
      [handleCastigarChofer, handleToggleEstado]
   );

   const estatusBadgeFormatter = useCallback(estatus => {
      if (estatus) {
         return <MDBBadge color='success px-4'>Activo</MDBBadge>;
      }
      return <MDBBadge color='danger'>Inactivo</MDBBadge>;
   }, []);

   const columns = useMemo(() => {
      return [
         {
            dataField: 'clave',
            text: 'Clave',
            headerAlign: 'center',
            align: 'center',
            headerClasses: 'font-weight-bold text-dark'
         },
         {
            dataField: 'nombreCompleto',
            text: 'Nombre',
            headerAlign: 'center',
            align: 'center',
            headerClasses: 'font-weight-bold text-dark'
         },
         {
            dataField: 'email',
            text: 'Correo Electrónico',
            headerAlign: 'center',
            align: 'center',
            headerClasses: 'font-weight-bold text-dark'
         },
         {
            dataField: 'activo',
            text: 'Estado',
            headerAlign: 'center',
            align: 'center',
            headerClasses: 'font-weight-bold text-dark',
            formatter: estatusBadgeFormatter
         },
         {
            dataField: 'tipoChofer',
            text: 'Tipo de chofer',
            headerAlign: 'center',
            align: 'center',
            headerClasses: 'font-weight-bold text-dark'
         },
         {
            dataField: 'options',
            text: 'Opciones',
            // isDummyField: true,
            headerAlign: 'center',
            align: 'center',
            headerClasses: 'font-weight-bold text-dark',
            formatter: optionsFormatter
         }
      ];
   }, [estatusBadgeFormatter, optionsFormatter]);

   return (
      <BootstrapTable
         bootstrap4
         condensed
         classes='choferes-table'
         keyField='idChofer'
         data={choferes}
         columns={columns}
         remote={{
            pagination: true
         }}
         loading={isTableLoading}
         overlay={overlayFactory({ spinner: true, background: 'rgba(192,192,192,0.3)' })}
         hover
         bordered={false}
         noDataIndication='No se encontraron registros.'
         rowClasses={'font-weight-bold'}
         pagination={paginationFactory({
            page: pagination.page, // Specify the current page.
            totalSize: pagination.totalSize, // Total data size.
            sizePerPage: pagination.sizePerPage, // Specify the size per page.
            withFirstAndLast: false, // hide the going to first and last page button
            alwaysShowAllBtns: true, // always show the next and previous page button
            prePageText: 'Anterior',
            nextPageText: 'Siguiente',
            hideSizePerPage: true, // hide the size per page dropdown
            hidePageListOnlyOnePage: true
         })}
         onTableChange={handleTableChange}
      />
   );
};

export default memo(ChoferesTabla);
